// src/components/Header.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Button from "react-bootstrap/Button";
import { ImCross, ImMenu, ImRedo2 } from "react-icons/im";
import { ToastContainer } from "react-toastify";

import { useAuth } from "../provider/authProvider";
import { ROLES, ROLE_GROUPS } from "../constants/roles";

import "react-toastify/dist/ReactToastify.css";
import "../styles/HeaderMenu.css";

/**
 * Valida si el rol actual se encuentra dentro
 * del grupo de roles permitido.
 */
const hasAccess = (allowedRoles, currentRole) => {
    if (currentRole === null || currentRole === undefined) {
        return false;
    }

    if (!Array.isArray(allowedRoles) || allowedRoles.length === 0) {
        return false;
    }

    return allowedRoles
        .map((role) => String(role))
        .includes(String(currentRole));
};

/**
 * URL base del frontend.
 *
 * Ejemplo:
 * REACT_APP_URL=""
 *
 * o:
 * REACT_APP_URL="/movilespay"
 */
const getAppUrl = () => {
    return String(process.env.REACT_APP_URL || "").replace(/\/$/, "");
};

/**
 * Concatena correctamente REACT_APP_URL con una ruta.
 */
const buildAppPath = (path = "") => {
    const appUrl = getAppUrl();
    const normalizedPath = String(path || "").startsWith("/")
        ? String(path)
        : `/${String(path || "")}`;

    return `${appUrl}${normalizedPath}`;
};

const Header = ({ namePage }) => {
    const {
        rol,
        user,
        name,
        sedeId,
        sedeName,
        logout,
    } = useAuth();

    const navigate = useNavigate();
    const location = useLocation();
    const menuRef = useRef(null);

    const [openSubmenus, setOpenSubmenus] = useState({});

    const roleStr =
        rol !== null && rol !== undefined
            ? String(rol)
            : null;

    const displayUser = String(name || user || "").trim();

    /**
     * Abre el menú móvil.
     */
    const showMenu = () => {
        menuRef.current?.classList.add("showMenu");
    };

    /**
     * Cierra el menú móvil.
     */
    const closeMenu = () => {
        menuRef.current?.classList.remove("showMenu");
    };

    /**
     * Abre o cierra un submenú.
     */
    const toggleSubmenu = (key) => {
        setOpenSubmenus((previous) => ({
            ...previous,
            [key]: !previous[key],
        }));
    };

    /**
     * Cierra el menú y los submenús cada vez
     * que cambia la ruta.
     */
    useEffect(() => {
        closeMenu();
        setOpenSubmenus({});
    }, [location.pathname]);

    /**
     * Cierra la sesión del usuario.
     */
    const handleLogout = () => {
        logout();

        navigate(
            "/evaluation-system-verify/login",
            { replace: true },
        );
    };

    /**
     * Estructura principal del menú.
     *
     * Los roles se obtienen desde ROLE_GROUPS
     * para evitar colocar permisos rígidos
     * directamente en este componente.
     */
    const menuItems = useMemo(
        () => [
            {
                key: "creditsPage",
                label: "Créditos",
                to: "/credits",
                allowedRoles: ROLE_GROUPS.CREDIT_TEAM,
            },

            {
                key: "registerPage",
                label: "Registro",
                to: "/register",
                allowedRoles: ROLE_GROUPS.REGISTER_TEAM,
            },

            {
                key: "cardsPage",
                label: "Tarjetas",
                to: "/cards",
                allowedRoles: ROLE_GROUPS.CARDS_TEAM,
            },

            {
                key: "verifyPage",
                label: "Verificar",
                to: "/verify-client",
                allowedRoles: ROLE_GROUPS.VERIFY_TEAM,
            },

            {
                key: "stockPage",
                label: "Stock",
                to: "/stock",
                allowedRoles: ROLE_GROUPS.STOCK_TEAM,
            },

            {
                key: "fmsPage",
                label: "FM",
                to: "/fms",
                allowedRoles: ROLE_GROUPS.FM_TEAM,
            },

            {
                key: "installmentPage",
                label: "Cuotas",
                to: "/installment",
                allowedRoles: ROLE_GROUPS.INSTALLMENT_TEAM,
                children: [
                    {
                        key: "receivablesPage",
                        label: "Cuotas por Cobrar",
                        to: "/receivables",
                        allowedRoles: [1, 8, 10],
                    },
                ],
            },
            {
                key: "receiptsPage",
                label: "Recibos",
                to: "/receipts",
                allowedRoles: ROLE_GROUPS.RECEIPTS_TEAM,
            },

            /*
            |--------------------------------------------------------------------------
            | USUARIOS / MI CUENTA
            |--------------------------------------------------------------------------
            */
            {
                key: "userPage",
                label: ["3", "8"].includes(roleStr) ? "Mi cuenta" : "Usuarios",
                to: roleStr === "8" ? "/academy" : "/user-roles",

                allowedRoles:
                    roleStr === "3"
                        ? ROLE_GROUPS.ADVISORS_TEAM
                        : roleStr === "8" ? [8] : ROLE_GROUPS.ADMIN_ONLY,

                children: [
                    {
                        key: "comisionesPage",
                        label: "Comisiones",
                        to: "/comisiones",
                        allowedRoles: ROLE_GROUPS.COMISIONES_TEAM,
                    },

                    {
                        key: "reportsPage",
                        label: "Reportes",
                        to: "/reports",
                        allowedRoles: ROLE_GROUPS.REPORTS_TEAM,
                    },
                    {
                        key: "academyPage",
                        label: "Academy",
                        to: "/academy",
                        allowedRoles: [1, 3, 8],
                    },
                ],
            },

            {
                key: "sedePage",
                label: "Sedes",
                to: "/sedes",
                allowedRoles: ROLE_GROUPS.ADMIN_ONLY,
            },

            {
                key: "warrantiesPage",
                label: "Garantías",
                to: "/warranties",
                allowedRoles: ROLE_GROUPS.WARRANTIES_TEAM,
            },

            {
                key: "payjoyAuditPage",
                label: "Auditoría",
                to: "/payjoy-audit",
                allowedRoles: ROLE_GROUPS.PAYJOY_AUDIT,
            },

            /*
            |--------------------------------------------------------------------------
            | INVENTARIO
            |--------------------------------------------------------------------------
            */
            {
                key: "inventoryPage",
                label: "Inventario",
                to: "/inventory",
                allowedRoles: ROLE_GROUPS.INVENTORY_TEAM
            },

            /*
            |--------------------------------------------------------------------------
            | CONTABILIDAD
            |--------------------------------------------------------------------------
            */
            {
                key: "accountingPage",
                label: "Contabilidad",
                to: "/accounting",
                allowedRoles: ROLE_GROUPS.ACCOUNTING_TEAM,

                children: [
                    {
                        key: "accountingMovementsPage",
                        label: "Movimientos",
                        to: "/accounting",
                        allowedRoles:
                            ROLE_GROUPS.ACCOUNTING_TEAM,
                    },

                    {
                        key: "accountingDashboardPage",
                        label: "Dashboard",
                        to: "/accounting/dashboard",
                        allowedRoles:
                            ROLE_GROUPS.ACCOUNTING_TEAM,
                    },

                    {
                        key: "accountingConfigPage",
                        label: "Configuración",
                        to: "/accounting/config",
                        allowedRoles:
                            ROLE_GROUPS.ACCOUNTING_TEAM,
                    },

                    {
                        key: "accountingScheduledPage",
                        label: "Programados",
                        to: "/accounting/scheduled",
                        allowedRoles:
                            ROLE_GROUPS.ACCOUNTING_TEAM,
                    },
                ],
            },
        ],
        [roleStr],
    );

    /**
     * Oculta los elementos y submenús
     * que el usuario no puede visualizar.
     */
    const visibleItems = useMemo(() => {
        return menuItems
            .map((item) => {
                const visibleChildren =
                    Array.isArray(item.children)
                        ? item.children.filter((child) =>
                            hasAccess(
                                child.allowedRoles,
                                roleStr,
                            ),
                        )
                        : [];

                const parentAccess = hasAccess(
                    item.allowedRoles,
                    roleStr,
                );

                if (
                    !parentAccess &&
                    visibleChildren.length === 0
                ) {
                    return null;
                }

                return {
                    ...item,
                    children: visibleChildren,
                };
            })
            .filter(Boolean);
    }, [menuItems, roleStr]);

    /**
     * Valida si una ruta se encuentra activa.
     */
    const isPathActive = (path) => {
        const currentPath = String(
            location.pathname || "",
        );

        const targetPath = String(path || "");

        if (targetPath === "/inventory") {
            return currentPath === "/inventory";
        }

        if (targetPath === "/accounting") {
            return currentPath === "/accounting";
        }

        return currentPath === targetPath;
    };

    /**
     * Renderiza cada elemento del menú.
     */
    const renderMenuItem = (item) => {
        const hasChildren =
            Array.isArray(item.children) &&
            item.children.length > 0;

        const isChildActive =
            hasChildren &&
            item.children.some(
                (child) =>
                    child.key === namePage ||
                    isPathActive(child.to),
            );

        const isActive =
            namePage === item.key ||
            isPathActive(item.to) ||
            isChildActive;

        const isOpen = Boolean(
            openSubmenus[item.key],
        );

        const isRole3UserMenu =
            roleStr === "3" &&
            item.key === "userPage";

        /**
         * Elemento sin submenú.
         */
        if (!hasChildren) {
            return (
                <li
                    key={item.key}
                    className={isActive ? "active" : ""}
                >
                    <Link to={buildAppPath(item.to)}>
                        {item.label}
                    </Link>
                </li>
            );
        }

        /**
         * Elemento con submenú.
         */
        return (
            <li
                key={item.key}
                className={[
                    "menuItemHasChildren",
                    isActive ? "active" : "",
                    isOpen ? "submenuOpen" : "",
                ]
                    .filter(Boolean)
                    .join(" ")}
            >
                <div className="menuParentRow">
                    {isRole3UserMenu ? (
                        <button
                            type="button"
                            className="menuParentLink"
                            onClick={(event) => {
                                event.preventDefault();
                                toggleSubmenu(item.key);
                            }}
                        >
                            {item.label}
                        </button>
                    ) : (
                        <Link
                            to={buildAppPath(item.to)}
                            className="menuParentLink"
                        >
                            {item.label}
                        </Link>
                    )}

                    <button
                        type="button"
                        className="submenuToggleBtn"
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            toggleSubmenu(item.key);
                        }}
                        aria-label={`Abrir submenú ${item.label}`}
                        aria-expanded={isOpen}
                    >
                        ▾
                    </button>
                </div>

                <ul className="submenuList">
                    {item.children.map((child) => {
                        const childActive =
                            namePage === child.key ||
                            isPathActive(child.to);

                        return (
                            <li
                                key={child.key}
                                className={
                                    childActive ? "active" : ""
                                }
                            >
                                <Link
                                    to={buildAppPath(child.to)}
                                >
                                    {child.label}
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            </li>
        );
    };

    return (
        <>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
            />

            <header className="sectionHeader headerMovilesPay">
                <div className="containerCustom containerFlexSpace headerMovilesPay__inner">
                    <div className="logoParent">
                        <Link
                            to={buildAppPath(
                                "/evaluation-system",
                            )}
                        >
                            <img
                                src={buildAppPath(
                                    "/logo-movilespay.png",
                                )}
                                alt="MovilesPay Logo"
                                className="logo"
                            />
                        </Link>
                    </div>

                    <button
                        type="button"
                        onClick={showMenu}
                        className={
                            roleStr ===
                                String(ROLES.ASESORES)
                                ? "toggleMenu"
                                : "toggleMenu normal"
                        }
                        aria-label="Abrir menú"
                    >
                        <ImMenu />
                    </button>

                    <div className="blockHeaderAccess">
                        <nav
                            ref={menuRef}
                            className="menu"
                            aria-label="Menú principal"
                        >
                            <button
                                type="button"
                                onClick={closeMenu}
                                className="mobileIconClose"
                                aria-label="Cerrar menú"
                            >
                                <ImCross />
                            </button>

                            <div className="mobileMenuBrand">
                                <img
                                    src={buildAppPath(
                                        "/logo-movilespay.png",
                                    )}
                                    alt="MovilesPay Logo"
                                />

                                <span>
                                    {displayUser || user}
                                </span>
                            </div>

                            <ul className="menuList">
                                {visibleItems.map(
                                    renderMenuItem,
                                )}
                            </ul>
                        </nav>

                        <div className="parentBtnLogOut">
                            <div>
                                <div className="headerConnect">
                                    <span className="connectText">
                                        Conectado:
                                    </span>

                                    <span className="username">
                                        {user}
                                    </span>
                                </div>

                                {roleStr === "3" && (
                                    <div className="headerSedeData">
                                        <div>
                                            Sede Actual:{" "}
                                            <strong>
                                                {sedeName ||
                                                    (sedeId
                                                        ? `ID ${sedeId}`
                                                        : "No seleccionada")}
                                            </strong>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="headerButton">
                                {/*
                                  La sede del asesor (rol 3) es administrada desde Usuarios.
                                  No se renderiza ningún control de cambio de sede para ese rol.
                                */}

                                <Button
                                    size="sm"
                                    variant="dark"
                                    onClick={handleLogout}
                                >
                                    <div>Logout</div>
                                    <ImRedo2 />
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </header>
        </>
    );
};

export default Header;
